import { ActionIcon, Button, Card, Group, Select, Stack, Table, Text } from "@mantine/core";
import { IconChevronRight, IconFileOff, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { ChargeContractApi } from "../../../apis";
import type { ChargeContract } from "../../../apis/chargeContract";
import { ContractStatusBadge } from "../../../components/billing/ContractStatusBadge";
import { EmptyState } from "../../../components/billing/EmptyState";
import { PermissionGate } from "../../../components/billing/PermissionGate";
import { TenantName } from "../../../components/billing/TenantName";
import { TenantSelect } from "../../../components/billing/TenantSelect";
import { PageLayout } from "../../../components/layout";
import { formatDate } from "../../../utils/format";

const useBreadcrumb = () => [
  { to: "/", label: "Эхлэл" },
  { to: "/billing", label: "Billing" },
  { label: "Гэрээ" }
];

const ContractsList = () => {
  const navigate = useNavigate();
  const breadcrumbs = useBreadcrumb();
  const [statusFilter, setStatusFilter] = useState<ChargeContract["status"] | "">("");
  const [tenantFilter, setTenantFilter] = useState<string>("");

  const swrKey = `swr.billing.contracts.list.${statusFilter}.${tenantFilter}`;
  const { data, isLoading } = useSWR(swrKey, () =>
    ChargeContractApi.list({
      status  : statusFilter || undefined,
      tenantId: tenantFilter || undefined,
      page    : 1,
      limit   : 50
    })
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: ChargeContract[] = (data as any)?.rows ?? (Array.isArray(data) ? (data as ChargeContract[]) : []);

  return (
    <PageLayout
      title="Платформын төлбөрийн гэрээ"
      subTitle="Тенант бүрийн платформ-тулгасан гэрээ ба бүрэлдэхүүн"
      breadcrumb={breadcrumbs}
      extra={[
        <PermissionGate key="create" code="BIL_002">
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate("/billing/contracts/new")}>
            Шинээр үүсгэх
          </Button>
        </PermissionGate>
      ]}
    >
      <Stack gap="md">
        <Group gap="sm">
          <TenantSelect value={tenantFilter} onChange={setTenantFilter} placeholder="Тенантаар шүүх" width={300} />
          <Select
            placeholder="Бүх төлөв"
            data={[
              { value: "DRAFT", label: "Ноорог" },
              { value: "ACTIVE", label: "Идэвхтэй" },
              { value: "SUSPENDED", label: "Түр зогссон" },
              { value: "TERMINATED", label: "Цуцласан" },
              { value: "ARCHIVED", label: "Архивласан" }
            ]}
            value={statusFilter || null}
            onChange={(v) => setStatusFilter((v as ChargeContract["status"]) || "")}
            clearable
            w={200}
          />
        </Group>

        <Card withBorder radius="md" padding={!isLoading && rows.length === 0 ? "md" : 0}>
          {!isLoading && rows.length === 0 ? (
            <EmptyState icon={<IconFileOff size={26} />} message="Гэрээ байхгүй байна." />
          ) : (
            <Table.ScrollContainer minWidth={720}>
              <Table verticalSpacing="sm" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Контракт №</Table.Th>
                    <Table.Th>Нэр</Table.Th>
                    <Table.Th>Тенант</Table.Th>
                    <Table.Th>Валют</Table.Th>
                    <Table.Th>Төлөв</Table.Th>
                    <Table.Th>Үйлчилгээ эхэлсэн</Table.Th>
                    <Table.Th w={48}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((c) => (
                    <Table.Tr
                      key={c.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/billing/contracts/${c.id}`)}
                    >
                      <Table.Td><Text size="sm" fw={500} style={{ fontVariantNumeric: "tabular-nums" }}>{c.contractNumber}</Text></Table.Td>
                      <Table.Td>{c.contractName}</Table.Td>
                      <Table.Td><TenantName id={c.tenantId} /></Table.Td>
                      <Table.Td>{c.currency}</Table.Td>
                      <Table.Td><ContractStatusBadge status={c.status} /></Table.Td>
                      <Table.Td style={{ fontVariantNumeric: "tabular-nums" }}>{formatDate(c.effectiveFrom)}</Table.Td>
                      <Table.Td>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label="Дэлгэрэнгүй"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/billing/contracts/${c.id}`);
                          }}
                        >
                          <IconChevronRight size={18} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Card>
      </Stack>
    </PageLayout>
  );
};

export default ContractsList;
